import { DepressionContentPage } from "@/components/DepressionContentPage";

const StartHere = () => (
  <DepressionContentPage
    seo={{
      title:
        "Start Here — Not Sure Which Depression Treatment to Explore | Neuro Wellness",
      description:
        "Not sure where to begin? Answer a few simple questions to find the right depression treatment resources and learn about your options at Neuro Wellness TMS Centers of America.",
      canonical: "/start-here",
      keywords:
        "depression treatment start here, which depression treatment, TMS or SPRAVATO, where to begin depression treatment",
    }}
    badge="Start Here"
    heroTitle="Not Sure Which Depression Treatment to Explore? Start Here."
    heroSubtitle="If you're not sure where to begin, you're in the right place. Answer a few simple questions below to find the resources most relevant to your situation. You won't be diagnosed or assigned a treatment — this is just a way to point you toward useful information."
    sections={[
      {
        heading: "Have you already been diagnosed with depression?",
        variant: "text",
        body: (
          <p>
            Whether you have a formal diagnosis or are just starting to explore,
            there's a path forward. If you have a diagnosis, treatment options
            may be clearer. If not, a consultation can help clarify next steps.
          </p>
        ),
      },
      {
        heading: "Have you tried medication?",
        variant: "text",
        body: (
          <p>
            If you've tried one or more antidepressants without adequate relief,
            you may be experiencing treatment-resistant depression. Treatments
            like TMS, SAINT®, and SPRAVATO® work through different mechanisms
            and may be worth understanding.
          </p>
        ),
      },
      {
        heading: "Have you tried therapy?",
        variant: "text",
        body: (
          <p>
            Therapy is an important part of treatment for many people. If
            therapy alone hasn't been enough, combining approaches or exploring
            interventional treatments may be worth discussing.
          </p>
        ),
      },
      {
        heading: "Are you researching for yourself or someone else?",
        variant: "links",
        links: [
          {
            title: "I'm researching for myself",
            href: "/depression-treatment-options",
            description: "Understand your treatment options",
          },
          {
            title: "I'm researching for my adult child",
            href: "/adult-child-with-depression",
            description: "A guide for parents",
          },
          {
            title: "I'm researching for my spouse or partner",
            href: "/helping-spouse-with-depression",
            description: "Supporting a partner with depression",
          },
          {
            title: "I'm researching for someone else I love",
            href: "/helping-someone-with-depression",
            description: "Family resources and support",
          },
        ],
      },
      {
        heading: "What are you most interested in learning about?",
        variant: "links",
        links: [
          {
            title: "Non-medication treatment",
            href: "/services/tms",
            description: "TMS uses magnetic stimulation, not medication",
          },
          {
            title: "Faster treatment schedules",
            href: "/saint",
            description: "Accelerated TMS and SAINT®",
          },
          {
            title: "TMS therapy",
            href: "/what-is-tms",
            description: "A plain explanation of TMS",
          },
          {
            title: "SAINT®",
            href: "/saint",
            description: "Individualized accelerated TMS",
          },
          {
            title: "SPRAVATO®",
            href: "/services/spravato",
            description: "Prescription esketamine nasal spray",
          },
          {
            title: "Insurance information",
            href: "/insurance-for-tms-depression-treatment",
            description: "Coverage and verification",
          },
          {
            title: "Self-pay options",
            href: "/depression-treatment-cost",
            description: "Cost and payment information",
          },
          {
            title: "I'm not sure",
            href: "/depression-treatment-consultation",
            description: "Start with a consultation",
          },
        ],
      },
    ]}
    faq={[
      {
        question: "Will this quiz diagnose me or recommend a treatment?",
        answer:
          "No. This page is designed to help you find relevant information. Diagnosis and treatment recommendations are made during a clinical consultation, not through a website.",
      },
      {
        question: "What if I'm not sure what I need?",
        answer:
          "That's completely okay — and it's exactly why we offer consultations. You don't need to know which treatment you want before calling us. We help you understand your options.",
      },
      {
        question: "Can I use this if I'm researching for someone else?",
        answer:
          "Yes. Many people who contact us are researching on behalf of a loved one. You can learn about options and share that information with the person you care about.",
      },
    ]}
    relatedLinks={[
      {
        title: "Depression Treatment Options",
        href: "/depression-treatment-options",
      },
      {
        title: "Compare Depression Treatments",
        href: "/compare-treatments",
      },
      {
        title: "What Happens During a Consultation",
        href: "/depression-treatment-consultation",
      },
      { title: "Depression Treatment FAQ", href: "/depression-treatment-faq" },
    ]}
    finalCtaHeading="Talk With Our Team"
    finalCtaText="Not sure where to start? That's the most common starting point. Call us — we'll help you figure out the next step, whatever that looks like for you."
  />
);

export default StartHere;
